import React, {useEffect, useMemo, useState} from 'react';
import {IntlProvider} from "react-intl";
import './assets/css/Dentobook.css';
import {AppRouter} from './components/router/AppRouter';
import {LOCALES} from "./i18n/locales";
import {messages} from "./i18n/messages";
import {DentobookApplicationContext} from "./context/DentobookApplicationContext";
import "@yaireo/tagify/dist/tagify.css";
import Authentication from "./components/Authentication";
import {Principal} from "./types/Principal";
import {Organization, SubscriptionResponse} from "./api-client";
import dayjs from "dayjs";
import { HelmetProvider } from 'react-helmet-async';
import {getUserApi, getOrganizationApi, getStripeApi, getFeaturesApi} from "./api/apiInstances";
import {setupAxiosInterceptors} from "./services/axiosInterceptor";
import {tokenService} from "./services/tokenService";
import {getSettings, UserSettings} from "./services/settingsService";
import {SubscriptionState} from "./constants/subscriptionStates";
import logger from "./utils/logger";
import {initializeLanguage} from "./utils/languageDetector";
import {identifyUser, trackEvent} from "./utils/analytics";

function App() {
    logger.debug('Environment variables:', process.env);

    // Initialize axios interceptors for JWT authentication
    useEffect(() => {
        setupAxiosInterceptors();
    }, []);

    const userApi = getUserApi();
    const organizationApi = getOrganizationApi();
    const stripeApi = getStripeApi();
    const featureApi = getFeaturesApi();

    const DEFAULT_LOCALE = LOCALES.ENGLISH;
    const [locale, setLocale] = useState(DEFAULT_LOCALE);
    const [principal, setPrincipal] = useState<Principal>();
    const [userOrganization, setUserOrganization] = useState<Organization>();
    const [selectSubscription, setSelectSubscription] = useState<number>(SubscriptionState.HIDDEN);
    const [initialLoad, setInitialLoad] = useState<boolean>(true);

    // Auto-detect language from country (IP-based) for first-time visitors
    useEffect(() => {
        initializeLanguage().then(detectedLanguage => {
            setLocale(detectedLanguage);
        });
    }, []);

    // Identify user in GA4 once principal loads after login. Fires once per session.
    // Also fires dashboard_viewed_first_time when the user has just accepted terms for
    // the first time — detected via a sessionStorage flag set by the terms acceptance
    // handlers in Login.tsx and Authentication.tsx.
    useEffect(() => {
        if (principal?.user?.id && principal?.user?.role) {
            identifyUser(principal.user.id, principal.user.role, locale);

            const isFirstSession = sessionStorage.getItem('dentobook_terms_just_accepted');
            if (isFirstSession) {
                sessionStorage.removeItem('dentobook_terms_just_accepted');
                trackEvent('dashboard_viewed_first_time');
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [principal?.user?.id]);

    useEffect(() => {
        document.title = 'Dentobook Dental Software';
        const handleWindowFocus = () => fetchPrincipal(userApi, principal);

        window.addEventListener('focus', handleWindowFocus);

        return () => {
            window.removeEventListener('focus', handleWindowFocus);
        };
    }, [userApi]);

    useEffect(() => {
        // Get organization ID directly from JWT token (stateless, no waiting for principal).
        // Require principal?.user before starting the chain: the async path needs principal
        // in scope to spread features onto it (setPrincipal({...principal, features})).
        // This effect depends on [principal], so it re-fires once Authentication.tsx sets
        // principal — at that point initialLoad is still true and the chain runs correctly.
        const orgId = tokenService.getOrganizationIdFromToken();

        if (orgId && initialLoad && principal?.user) {
            organizationApi.fetchOrganizationById(orgId)
                .then(async (organizationResponse) => {
                    setUserOrganization(organizationResponse.data!);
                    setInitialLoad(false);

                    // Load user's language preference from backend
                    try {
                        const settingsData = await getSettings();
                        const userLang = (settingsData.settings as UserSettings).language || 'en';
                        const normalizedLang = userLang.toLowerCase().split('-')[0]; // 'EN' -> 'en', 'en-US' -> 'en'
                        setLocale(normalizedLang);
                        localStorage.setItem('dentobook-lang', normalizedLang);
                    } catch (error) {
                        logger.error('Failed to load user settings, using default:', error);
                        // Fallback to localStorage or default
                        const savedLang = localStorage.getItem('dentobook-lang') || 'en';
                        const normalizedLang = savedLang.toLowerCase().split('-')[0];
                        setLocale(normalizedLang);
                    }

                    let subscription: SubscriptionResponse | null = null;
                    try {
                        const { data } = await organizationApi.getCurrentSubscription();
                        subscription = data;
                    } catch (e) {
                        // no subscription or 404 — treat as null
                        subscription = null;
                    }
                    
                    const subscriptionExpiration = subscription?.expiration
                        ? dayjs(subscription.expiration)
                        : null;

                    if (!subscription?.id || subscriptionExpiration?.isBefore(dayjs())) {
                        // Check if Stripe is enabled globally (STRIPE_ENABLED environment variable) using generated API client
                        let stripeEnabled = false;
                        try {
                            const stripeApi = getStripeApi();
                            const response = await stripeApi.isStripeEnabled();
                            stripeEnabled = response.data?.enabled ?? false;
                        } catch (error) {
                            logger.error('Error checking Stripe enabled status:', error);
                        }

                        if (!stripeEnabled) {
                            // ARCHITECTURE NOTE: Subscriptions are used for feature gating, not just payments
                            // When Stripe is disabled, we still need a subscription to enable features
                            //
                            // Feature System Architecture:
                            // - Features come from the organization's subscription's plan
                            // - No subscription = no features = app mostly non-functional
                            // - To enable all features when payments disabled: auto-create PAID subscription
                            //
                            // API calls made here:
                            // 1. GET /api/plans/ - Find the PAID plan
                            // 2. POST /api/subscriptions/ - Create subscription with PAID plan
                            // 3. GET /api/features/ - Fetch features for the organization
                            //
                            // TODO: Consider separating feature management from subscription/payment system
                            // See backend-python/TODO.md for architectural discussion
                            logger.info('Stripe disabled (STRIPE_ENABLED=false) - loading features (all orgs have subscriptions)');
                            try {
                                // All organizations have subscriptions - just fetch features
                                const orgIdFromToken = tokenService.getOrganizationIdFromToken();
                                if (!orgIdFromToken || !principal?.user) {
                                    // Should not happen: outer guard already requires principal?.user
                                    logger.warn('Cannot fetch features: organizationId or principal is missing');
                                    return;
                                }
                                const featuresResponse = await featureApi.fetchFeaturesByOrganization(orgIdFromToken);
                                const featuresArray = featuresResponse.data.map((feature: any) => feature.name);
                                setPrincipal({...principal, features: featuresArray});

                                logger.info('Features loaded successfully');
                                setSelectSubscription(SubscriptionState.HIDDEN);
                            } catch (error) {
                                logger.error('Error loading features:', error);
                            }
                        } else {
                            // Stripe enabled - check configuration
                            try {
                                const stripeResponse = await stripeApi.getPublishableKey();
                                const publishableKey = stripeResponse.data?.publishableKey;

                                if (!publishableKey || publishableKey.trim() === '') {
                                    // Stripe key not configured - load features (all orgs have subscriptions)
                                    logger.info('Stripe key not configured - loading features');
                                    const orgIdFromToken = tokenService.getOrganizationIdFromToken();
                                    if (!orgIdFromToken || !principal?.user) {
                                        // Should not happen: outer guard already requires principal?.user
                                        logger.warn('Cannot fetch features: organizationId or principal is missing');
                                        return;
                                    }
                                    const featuresResponse = await featureApi.fetchFeaturesByOrganization(orgIdFromToken);
                                    const featuresArray = featuresResponse.data.map((feature: any) => feature.name);
                                    setPrincipal({...principal, features: featuresArray});

                                    logger.info('Features loaded successfully');
                                    setSelectSubscription(SubscriptionState.HIDDEN);
                                } else {
                                    // Stripe configured - show Choose Plan screen
                                    setSelectSubscription(SubscriptionState.SHOW_PLAN_SELECTION);
                                }
                            } catch (error) {
                                logger.error('Error checking Stripe status:', error);
                                // On error, show Choose Plan screen as fallback
                                setSelectSubscription(SubscriptionState.SHOW_PLAN_SELECTION);
                            }
                        }
                    }
                })
                .catch((error) => {
                    logger.error("Error fetching organization data:", error);
                });
        }
    }, [principal]);


    const contextValue = useMemo(
        () => ({
            locale,
            setLocale,
            principal,
            setPrincipal,
            selectSubscription,
            setSelectSubscription,
            userOrganization,
            setUserOrganization,
        }),
        [locale, setLocale, principal, setPrincipal, selectSubscription, setSelectSubscription, userOrganization, setUserOrganization]
    );

    // Debug: Log locale and messages availability at IntlProvider mount
    logger.debug(`IntlProvider mount: locale="${locale}", messages[locale] defined:`, !!messages[locale]);

    return (
        <DentobookApplicationContext.Provider value={contextValue}>
            <HelmetProvider>
                <IntlProvider
                    messages={messages[locale]}
                    locale={locale}
                    defaultLocale={DEFAULT_LOCALE}
                >
                    <Authentication>
                        <AppRouter/>
                    </Authentication>
                </IntlProvider>
            </HelmetProvider>
        </DentobookApplicationContext.Provider>
    );
}

async function fetchPrincipal(userApi: any, principal: Principal | undefined) {
    if (!principal?.user) return;

    try {
        await userApi.fetchPrincipal();
    } catch {
        // Token is invalid, clear it and redirect to login
        tokenService.clearTokens();
        window.location.href = "/login";
    }
}

export default App;